import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import UserContext from "common/Context/UserContext";
import ParentNodeTreePicker from "common/Components/ParentNodeTreePicker/ParentNodeTreePicker";
import { GenericForm } from "common/Components/GenericForm";
import { SideDrawerLayout, SIDE_DRAWER_UI } from "common/Components/SideDrawer";
import {
  buildParentSelectOptionsForRow,
  filterNodeTypeOptionsForTagEdit,
  getRowSourceConnector,
  isNodeTypeLockedForSourceTag,
  isWmAppConnector,
} from "pages/DataManagement/NodeList/nodeListRowsMapper";
import { TAG_SOURCE_CONNECTORS } from "pages/Admin/Customer/Customers.constants";
import { TAGS_UI } from "pages/DataManagement/NodeList/NodeList.constants";
import {
  DrawerNote,
  ErrorText,
  FieldsStack,
  FieldLabel,
  ReadOnlyValue,
} from "./BulkEditNodeListDialog.styles";
import { FieldBlock, FieldHint } from "styles/formStyleComponents";
import { validateNodeNameForSave } from "common/utils/nodeNameValidation";

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

function createInitialBulkFieldState() {
  return {
    nodeName: "",
    nodeType: "",
    nodeSubType: "",
    parentNode: "",
    parentNodeId: null,
  };
}

function commonNodeTypeFromSelectedRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const first = String(rows[0]?.nodeType ?? "");
  if (!first) return "";
  if (!rows.every((r) => String(r?.nodeType ?? "") === first)) return "";
  return first;
}

function getTagNodeTypeOption(nodeTypeOptions) {
  const fromReference = (nodeTypeOptions ?? []).find(
    (option) => String(option?.value ?? "").toLowerCase() === "tag"
  );
  return fromReference ?? { value: "tag", label: "Tag" };
}

export default function BulkEditNodeListDialog({
  open,
  onClose,
  selectedRows,
  onApply,
  nodeTypeOptions,
  subtypeOptionsByNodeType = {},
  referencePayload = null,
  parentCandidates = [],
}) {
  const { getCustomerName } = useContext(UserContext);
  const customerName = getCustomerName?.() ?? "";
  const [bulkFields, setBulkFields] = useState(() => createInitialBulkFieldState());
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationError, setValidationError] = useState("");

  const selectedRowCount = selectedRows?.length ?? 0;
  const isAllUncontextualizedSelection =
    selectedRowCount > 0 &&
    (selectedRows ?? []).every((r) => Boolean(r?.uncontextualized));
  const isNodeTypeDisabled =
    isAllUncontextualizedSelection ||
    (selectedRowCount > 0 &&
      (selectedRows ?? []).every((r) => isNodeTypeLockedForSourceTag(r)));
  const isAllNonWmAppLockedSelection =
    selectedRowCount > 0 &&
    !isAllUncontextualizedSelection &&
    (selectedRows ?? []).every(
      (row) =>
        isNodeTypeLockedForSourceTag(row) &&
        !isWmAppConnector(getRowSourceConnector(row))
    );
  const bulkSourceConnectorFilter = useMemo(() => {
    const rows = selectedRows ?? [];
    if (
      rows.length > 0 &&
      rows.every((row) => isWmAppConnector(getRowSourceConnector(row)))
    ) {
      return TAG_SOURCE_CONNECTORS.WM_APP;
    }
    return "";
  }, [selectedRows]);
  const contextualizeNodeTypeOption = useMemo(
    () => getTagNodeTypeOption(nodeTypeOptions),
    [nodeTypeOptions]
  );

  const editableNodeTypeOptions = useMemo(
    () =>
      filterNodeTypeOptionsForTagEdit(
        nodeTypeOptions,
        bulkSourceConnectorFilter
      ),
    [bulkSourceConnectorFilter, nodeTypeOptions]
  );

  const sharedSelectedNodeType = useMemo(
    () => commonNodeTypeFromSelectedRows(selectedRows),
    [selectedRows]
  );

  const effectiveNodeType =
    bulkFields.nodeType ||
    (isAllUncontextualizedSelection || isAllNonWmAppLockedSelection
      ? String(contextualizeNodeTypeOption.value)
      : sharedSelectedNodeType);

  const hasNodeTypeValue = Boolean(effectiveNodeType);

  const bulkSubtypeSelectOptions = useMemo(
    () =>
      effectiveNodeType
        ? subtypeOptionsByNodeType[String(effectiveNodeType)] ?? []
        : [],
    [effectiveNodeType, subtypeOptionsByNodeType]
  );

  const bulkParentSelectOptions = useMemo(
    () =>
      effectiveNodeType
        ? buildParentSelectOptionsForRow(
            { nodeType: effectiveNodeType },
            referencePayload,
            parentCandidates,
            customerName
          )
        : [],
    [effectiveNodeType, referencePayload, parentCandidates, customerName]
  );

  useEffect(() => {
    if (!open) {
      setBulkFields(createInitialBulkFieldState());
      setFieldErrors({});
      setValidationError("");
      return;
    }
    setBulkFields({
      ...createInitialBulkFieldState(),
      nodeType:
        isAllUncontextualizedSelection || isAllNonWmAppLockedSelection
          ? String(contextualizeNodeTypeOption.value)
          : "",
    });
    setFieldErrors({});
    setValidationError("");
  }, [
    contextualizeNodeTypeOption.value,
    isAllNonWmAppLockedSelection,
    isAllUncontextualizedSelection,
    open,
    selectedRows,
  ]);

  const displayedBulkFields = useMemo(
    () => ({
      ...bulkFields,
      nodeType:
        bulkFields.nodeType ||
        (isAllUncontextualizedSelection || isAllNonWmAppLockedSelection
          ? String(contextualizeNodeTypeOption.value)
          : isWmAppConnector(bulkSourceConnectorFilter) &&
              sharedSelectedNodeType.toLowerCase() === "tag"
            ? ""
            : sharedSelectedNodeType),
    }),
    [
      bulkFields,
      bulkSourceConnectorFilter,
      contextualizeNodeTypeOption.value,
      isAllNonWmAppLockedSelection,
      isAllUncontextualizedSelection,
      sharedSelectedNodeType,
    ]
  );

  const mergeBulkFields = (patch) =>
    setBulkFields((previous) => ({ ...previous, ...patch }));

  const handleApply = () => {
    setFieldErrors({});
    setValidationError("");
    const patch = {};

    if (isAllUncontextualizedSelection) {
      const nextErrors = {};
      const nodeNameError = validateNodeNameForSave(bulkFields.nodeName);
      if (nodeNameError) {
        nextErrors.nodeName = nodeNameError;
      }
      if (!(bulkFields.nodeType ?? "").trim()) {
        nextErrors.nodeType = TAGS_UI.validation.nodeTypeRequired;
      }
      if (bulkFields.parentNodeId == null || bulkFields.parentNodeId === "") {
        nextErrors.parentNode = TAGS_UI.validation.parentRequired;
      }
      if (Object.keys(nextErrors).length) {
        setFieldErrors(nextErrors);
        return;
      }
      patch.nodeName = bulkFields.nodeName.trim();
      patch.parentNodeId = bulkFields.parentNodeId;
      if ((bulkFields.nodeSubType ?? "").trim()) {
        if (!bulkSubtypeSelectOptions.length) {
          setValidationError(
            "No subtypes for this node type. Clear subtype or pick another type."
          );
          return;
        }
        patch.nodeSubType = bulkFields.nodeSubType.trim();
      }
      onApply(patch);
      onClose();
      return;
    }

    if ((bulkFields.nodeName ?? "").trim()) {
      const nodeNameError = validateNodeNameForSave(bulkFields.nodeName);
      if (nodeNameError) {
        setFieldErrors({ nodeName: nodeNameError });
        return;
      }
      patch.nodeName = bulkFields.nodeName.trim();
    }

    if ((bulkFields.nodeType ?? "").trim()) {
      patch.nodeType = bulkFields.nodeType.trim();
    }
    if ((bulkFields.nodeSubType ?? "").trim()) {
      if (!bulkSubtypeSelectOptions.length) {
        setValidationError(
          "No subtypes for this node type. Clear subtype or pick another type."
        );
        return;
      }
      patch.nodeSubType = bulkFields.nodeSubType.trim();
    }
    if (bulkFields.parentNodeId != null && bulkFields.parentNodeId !== "") {
      if (!bulkParentSelectOptions.length) {
        setValidationError(
          "No valid parents for this node type. Clear parent or pick another type."
        );
        return;
      }
      patch.parentNodeId = bulkFields.parentNodeId;
    }

    if (!Object.keys(patch).length) {
      setValidationError("Set at least one field to update.");
      return;
    }

    onApply(patch);
    onClose();
  };

  const title = isAllUncontextualizedSelection
    ? TAGS_UI.contextualizeTagsTitle
    : TAGS_UI.bulkEditTitle;
  const subtitle = TAGS_UI.bulkEditSubtitle(selectedRowCount);
  const primaryLabel = isAllUncontextualizedSelection
    ? selectedRowCount === 1
      ? "Contextualize Node"
      : `Contextualize (${selectedRowCount})`
    : "Apply";

  const nodeTypeOptionsForSelect =
    isAllUncontextualizedSelection || isAllNonWmAppLockedSelection
      ? [contextualizeNodeTypeOption]
      : editableNodeTypeOptions;
  const nodeSubtypePlaceholder = !hasNodeTypeValue
    ? "Select a Node Type"
    : !bulkSubtypeSelectOptions.length
      ? "No Subtypes for this Type"
      : "Subtype";

  const bulkEditFormFields = useMemo(
    () => [
      {
        name: "tagsSelected",
        type: "custom",
        customRender: () => (
          <FieldBlock>
            <FieldLabel component="div">Nodes selected</FieldLabel>
            <ReadOnlyValue component="div" data-testid="bulk-tags-selected">
              {selectedRowCount}
            </ReadOnlyValue>
          </FieldBlock>
        ),
      },
      {
        name: "nodeName",
        type: "text",
        label: "Name",
        placeholder: "Name",
        hideLabel: true,
        inputProps: { "data-testid": "bulk-node-name" },
      },
      {
        name: "nodeType",
        type: "singleSelect",
        label: "Type",
        placeholder: "Type",
        options: nodeTypeOptionsForSelect,
        disabled: isNodeTypeDisabled,
        disableClearable: isNodeTypeDisabled,
        inputProps: { "data-testid": "bulk-node-type" },
      },
      {
        name: "nodeSubType",
        type: "singleSelect",
        label: "Subtype",
        placeholder: nodeSubtypePlaceholder,
        options: bulkSubtypeSelectOptions,
        disabled: !hasNodeTypeValue,
        inputProps: { "data-testid": "bulk-node-subtype" },
      },
      {
        name: "parentNode",
        type: "custom",
        customRender: (_value, _onChange, _field, error) => (
          <FieldBlock>
            <ParentNodeTreePicker
              label="Parent"
              nodeType={effectiveNodeType}
              referencePayload={referencePayload}
              parentCandidates={parentCandidates}
              disabled={!hasNodeTypeValue}
              valueId={bulkFields.parentNodeId}
              onChange={(patch) => {
                mergeBulkFields(patch);
                if (fieldErrors.parentNode) {
                  setFieldErrors((previous) => {
                    const next = { ...previous };
                    delete next.parentNode;
                    return next;
                  });
                }
              }}
              error={error}
              inputTestId="bulk-open-select-parent"
              placeholder="Parent"
            />
            {!error ? (
              <FieldHint component="p">{TAGS_UI.bulkParentHint}</FieldHint>
            ) : null}
          </FieldBlock>
        ),
      },
    ],
    [
      bulkFields.parentNodeId,
      bulkSubtypeSelectOptions,
      effectiveNodeType,
      fieldErrors.parentNode,
      hasNodeTypeValue,
      isNodeTypeDisabled,
      nodeSubtypePlaceholder,
      nodeTypeOptionsForSelect,
      parentCandidates,
      referencePayload,
      selectedRowCount,
    ]
  );

  const handleBulkFormChange = (name, value) => {
    if (name === "nodeName") {
      mergeBulkFields({ nodeName: value });
      if (fieldErrors.nodeName) {
        setFieldErrors((previous) => {
          const next = { ...previous };
          delete next.nodeName;
          return next;
        });
      }
      return;
    }
    if (name === "nodeType") {
      mergeBulkFields({
        nodeType: value ?? "",
        nodeSubType: "",
        parentNode: "",
        parentNodeId: null,
      });
      setFieldErrors((previous) => {
        const next = { ...previous };
        delete next.nodeType;
        delete next.parentNode;
        return next;
      });
      return;
    }
    if (name === "nodeSubType") {
      mergeBulkFields({ nodeSubType: value ?? "" });
    }
  };

  return (
    <SideDrawerLayout
      open={open}
      onClose={onClose}
      title={title}
      titleId="bulk-edit-title"
      description={subtitle}
      onSubmit={handleApply}
      submitLabel={primaryLabel}
      cancelLabel={SIDE_DRAWER_UI.cancelLabel}
      submitTestId="bulk-edit-apply"
      testId="bulk-edit-dialog"
    >
      {validationError ? (
        <ErrorText component="div">{validationError}</ErrorText>
      ) : null}

      <GenericForm
        sections={[
          {
            fields: bulkEditFormFields,
            wrapper: FieldsStack,
          },
        ]}
        values={displayedBulkFields}
        onChange={handleBulkFormChange}
        errors={fieldErrors}
        showBorder={false}
      />

      <DrawerNote component="p">{TAGS_UI.bulkEditNote}</DrawerNote>
    </SideDrawerLayout>
  );
}

const subtypeMapShape = PropTypes.objectOf(PropTypes.arrayOf(optionShape));

BulkEditNodeListDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired,
  nodeTypeOptions: PropTypes.arrayOf(optionShape).isRequired,
  subtypeOptionsByNodeType: subtypeMapShape,
  referencePayload: PropTypes.object,
  parentCandidates: PropTypes.arrayOf(PropTypes.object),
};

BulkEditNodeListDialog.defaultProps = {
  selectedRows: [],
  subtypeOptionsByNodeType: {},
  referencePayload: null,
  parentCandidates: [],
};
